import { useTheme } from 'hooks';
import React from 'react';
import styled from 'styled-components';
import { lightThemeBackground, darkThemeBackground } from 'hooks/useTheme/useTheme.util';

const Progress = styled.div`
  --size: 4.625rem;
  cursor: pointer;
  width: var(--size);
  height: var(--size);
  font-size: 0.625rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ProgressCircle = styled.svg<{ background: string }>`
  transform: rotate(-90deg);
  fill: ${({ background }) => background};
  font-size: 1rem;
`;

const ProgressCircleBack = styled.circle`
  stroke: #607289;
  stroke-width: 2px;
`;

const ProgressCircleProgress = styled.circle<{ fg: string, completedPercentage: number }>`
  fill: none; 
  stroke: #7E3451;
  stroke: ${({ fg }) => fg};
  stroke-width: 2px;  
  stroke-dasharray: ${({ completedPercentage }) => `${(completedPercentage * 13) / 100}em 13.75em`};    
  stroke-dashoffset: 0px;
  transition: stroke-dasharray 0.7s linear 0s;
`;

const ProgressText = styled.div<{ color: string }>`
  position: absolute;
  text-align: center;
  font-weight: 700;
  color: ${({ color }) => color};

  & > .svg-wrapper.size--small {
    padding: 0 0.438rem;
    display: flex;
    justify-content: center;
  }
`;

const StepsText = styled.div`
  padding-top: 0.5rem;
  line-height: 0.625rem;
  font-size: 0.625rem;
`;

type ProgressBarPropTypes = {
  currentStep?: number | null;
  totalSteps?: number | null;
  textNode?: string | React.ReactNode;
  icon: React.ReactSVGElement | React.ReactElement;
  darkMode?: boolean;
};

export const ProgressBar: React.FC<ProgressBarPropTypes> = ({ currentStep, totalSteps, icon, textNode, darkMode }) => {
  const { styles } = useTheme();
  let progressPercentage = 0;

  // If totalSteps or currentStep are not defined, do not display completed percentage on the dial
  if (totalSteps && currentStep) {
    const completedSteps = currentStep - 1;
    const allSteps = totalSteps;
    progressPercentage = (completedSteps / allSteps) * 100;
  }

  return (
    <Progress>
      <ProgressCircle
        background={darkMode ? darkThemeBackground.colorBackgroundBase : lightThemeBackground.colorBackgroundBase}
        height="4.625rem"
        width="4.625rem"
        xmlns="http://www.w3.org/2000/svg"
      >
        <ProgressCircleBack cx="50%" cy="50%" r="2.188rem" />
        <ProgressCircleProgress fg={styles.colorPrimaryButtonBackground} completedPercentage={progressPercentage} cx="50%" cy="50%" r="2.188rem" />
      </ProgressCircle>
      <ProgressText
        data-progress={progressPercentage}
        // NOTE: We have specific 'Dark mode' in shoot mode and dark mode might not be configured
        //       I'll take colors from statically defined background colors
        color={darkMode ? lightThemeBackground.colorBackgroundBase : darkThemeBackground.colorBackgroundBase}
      >
        {icon}
        <StepsText>
          {textNode || (
          <>
            <span className="current">{currentStep}</span>
            &nbsp;/&nbsp;
            <span className="total">{totalSteps}</span>
          </>)}
        </StepsText>
      </ProgressText>
    </Progress>
  );
};
